import { Box, Button, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveTransportStatus } from '../../../../../api/transports';
import ConfirmDialogModal from '../../../../../reusable-components/confirm-dialog/ConfirmDialogModal';
import Iconify from '../../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import { REQUEST_STATUS, REQUEST_STATUS_NAMES, STATUS_BUTTONS_ACTION_CONFIG } from '../../statusEnums';
import { buildTransportStatusPayload } from '../utils';

ActionBar.propTypes = {
  request: PropTypes.object,
  setRequest: PropTypes.func,
  isEditing: PropTypes.bool,
  allowEdit: PropTypes.bool,
};

export default function ActionBar({ request, setRequest, isEditing, allowEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmAction, setConfirmAction] = useState(null);

  // Helper to check required fields for approval
  const getMissingApprovalFields = (req) => {
    const missing = [];
    if (!req.pickupTime) missing.push('Pick up time');
    if (!req.appointmentDateTime) missing.push('Appointment time');
    if (req.isOutsourced === true) {
      if (!req.outsourceCompanyName) missing.push('Outsource company');
    } else {
      if (!req.assignedDriverId) missing.push('Driver');
      if (!req.vehicleLabel) missing.push('Vehicle');
    }
    return missing;
  };

  const handleStatusUpdate = async (newStatusId) => {
    if (newStatusId === REQUEST_STATUS.APPROVED) {
      const missingFields = getMissingApprovalFields(request);
      if (missingFields.length > 0) {
        enqueueSnackbar(`Cannot approve request. Missing: ${missingFields.join(', ')}`, { variant: 'error' });
        return;
      }
    }
    try {
      const statusData = {
        id: request.id,
        requestStatusId: newStatusId,
        occurrenceDate: request.occurrenceDate,
        occurrenceId: request.occurrenceId,
      };
      const response = await saveTransportStatus(buildTransportStatusPayload(statusData));
      setRequest(response.data);
      const statusName = REQUEST_STATUS_NAMES[newStatusId];
      enqueueSnackbar(`Transport request is ${statusName.toLowerCase()}`, { variant: 'success' });
    } catch (error) {
      // Show backend error if present
      const errorMsg =
        error?.error || error?.message || error?.toString() || 'Failed to update transport request status';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const actionObj = STATUS_BUTTONS_ACTION_CONFIG[confirmAction];
    if (actionObj) {
      await handleStatusUpdate(actionObj.status);
    }
    setConfirmAction(null);
  };

  const handleBackClick = () => {
    const calendarState = location.state?.calendarState;
    if (calendarState) {
      navigate('/dashboard/transports/calendar', { state: { calendarState } });
    } else {
      navigate('/dashboard/transports/calendar');
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Button sx={{ py: 0, fontWeight: 400 }} onClick={handleBackClick} color="secondary">
          <Iconify icon="mdi:chevron-left" /> Back to Calendar
        </Button>
        {allowEdit && (
          <>
            <Box display="flex" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={1}>
              {Object.entries(STATUS_BUTTONS_ACTION_CONFIG).map(
                ([actionKey, actionObj]) =>
                  actionObj.show(request.requestStatusId) && (
                    <Tooltip
                      key={actionKey}
                      title={'Please save or cancel your edits'}
                      placement="top"
                      arrow
                      disableHoverListener={!isEditing}
                    >
                      <Button
                        variant={actionObj.variant}
                        size="small"
                        sx={{ fontWeight: 400, fontSize: '14px' }}
                        color="secondary"
                        onClick={() => setConfirmAction(actionKey)}
                        disabled={isEditing}
                      >
                        {actionObj.label}
                      </Button>
                    </Tooltip>
                  )
              )}
            </Box>

            <ConfirmDialogModal
              title={'Confirmation'}
              content={confirmAction ? STATUS_BUTTONS_ACTION_CONFIG[confirmAction]?.dialog : ''}
              action={handleConfirmAction}
              color={confirmAction ? STATUS_BUTTONS_ACTION_CONFIG[confirmAction]?.color : 'primary'}
              actionButton={confirmAction ? STATUS_BUTTONS_ACTION_CONFIG[confirmAction]?.button : ''}
              setOpenConfirm={() => setConfirmAction(null)}
              openConfirm={!!confirmAction}
            />
          </>
        )}
      </Box>
    </>
  );
}
